import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, MapPin, Users, Building2 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function VenuesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');


  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await axios.get(`${API}/public/venues?limit=50`);
        if (response.data.items?.length > 0) {
          setVenues(response.data.items);
        } else {
          setVenues([]);
        }
      } catch (error) {
        console.error('Error fetching venues:', error);
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  const venueTypes = ['all', 'Club', 'Rooftop', 'Beach Club', 'Hotel', 'Arena'];

  const filteredVenues = venues.filter(venue => {
    const matchesFilter = filter === 'all' || venue.venue_type?.includes(filter);
    const matchesSearch = searchTerm === '' || 
      (venue.name?.[lang] || venue.name?.es || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="venues-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              {t('venues.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('venues.title')}
            </motion.h1>

            {/* Filters */}
            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {venueTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setFilter(type)}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                      filter === type
                        ? 'bg-white text-black'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`filter-${type}`}
                  >
                    {type === 'all' ? t('venues.filter_all') : type}
                  </button>
                ))}
              </div>

              <div className="relative w-full md:w-auto">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-premium pl-12 pr-4 py-3 w-full md:w-64"
                  data-testid="search-input"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Venues Grid */}
      <section className="section-dark py-16" data-testid="venues-grid">
        <div className="container-premium">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] loading-skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredVenues.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredVenues.map((venue) => (
                <motion.div key={venue.venue_id || venue.slug} variants={fadeInUp}>
                  <Link to={`/venues/${venue.slug}`} className="block group" data-testid={`venue-${venue.slug}`}>
                    <div className="premium-card overflow-hidden">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={venue.cover_image || 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=600&q=80'}
                          alt={venue.name?.[lang] || venue.name?.es}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        {venue.featured && (
                          <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-black text-xs font-medium rounded-full">
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {venue.venue_type?.slice(0, 2).map((type) => (
                            <span key={type} className="text-[#C0C0C0] text-xs tracking-wider uppercase">
                              {type}
                            </span>
                          ))}
                        </div>
                        <h3 className="font-display text-xl text-white mb-2">
                          {venue.name?.[lang] || venue.name?.es}
                        </h3>
                        <div className="flex items-center justify-between text-white/50 text-sm">
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {venue.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {venue.capacity?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <Building2 size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50">No se encontraron espacios</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-subtle" data-testid="venues-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl text-white mb-4">
              ¿Tienes un espacio increíble?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 mb-8">
              Regístrate y forma parte de nuestra red de venues
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/register" className="btn-gold">
                Registrar mi Venue
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
